import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Lock, ArrowRight, CheckCircle2, AlertTriangle, User, Calendar, Percent, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { formatCurrency } from '@/utils/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvitePage() {
  const { token: inviteToken } = useParams();
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const { 
    t, 
    getTemplateName, 
    getTemplateLabel, 
    getTemplateFieldLabel, 
    formatCurrency: formatCurrencyWithLang 
  } = useLanguage();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/agreements/invite/${inviteToken}`)
      .then(res => setInvite(res.data))
      .catch(err => setError(err.response?.data?.detail || t('errors.invalid_invite')))
      .finally(() => setLoading(false));
  }, [inviteToken, t]);

  const handleAccept = async () => {
    if (!user) {
      navigate(`/login?redirect=/invite/${inviteToken}`);
      return;
    }
    setAccepting(true);
    try {
      const res = await api.post(`/agreements/invite/${inviteToken}/accept`);
      toast.success(t('loan.invite.success_join', { role: getTemplateLabel(templateType, 'secondary') }));
      navigate(`/agreements/${res.data.loan_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.server_error'));
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" data-testid="invite-loading">
        <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6" data-testid="invite-error">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="w-16 h-16 bg-destructive/10 rounded-sm mx-auto flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-destructive" />
          </div>
          <h1 className="font-serif text-2xl font-bold">{t('errors.invalid_invite')}</h1>
          <p className="text-muted-foreground">{error}</p>
          <Button onClick={() => navigate('/login')} className="bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-6 rounded-sm uppercase tracking-wider font-semibold text-xs">
            {t('auth.login.title')}
          </Button>
        </div>
      </div>
    );
  }

  const loan = invite?.loan;
  const lender = invite?.lender;
  const templateType = loan?.agreement_template || 'LOAN';
  const templateName = getTemplateName(templateType);
  const lenderCurrency = lender?.currency || 'INR';
  const hasInterest = loan?.interest_enabled !== false && loan?.interest_rate > 0;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12" data-testid="invite-page">
      <div className="max-w-lg w-full space-y-8">
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-2.5 mb-4">
            <div className="w-8 h-8 bg-primary rounded-sm flex items-center justify-center">
              <Lock className="w-4 h-4 text-primary-foreground" />
            </div>
            <span className="font-serif font-bold text-xl tracking-tight">Pakka</span>
          </div>
          <Badge variant="outline" className="mx-auto mb-2 text-xs font-medium uppercase tracking-wider">
            {templateName}
          </Badge>
          <h1 className="font-serif text-3xl font-bold tracking-tight">{templateName} {t('loan.invite.title')}</h1>
          <p className="text-muted-foreground text-sm">
            {t('loan.invite.description_prefix') || 'You have been invited to join as the'} <strong>{getTemplateLabel(templateType, 'secondary')}</strong>
          </p>
        </div>

        {invite?.used && (
          <div className="bg-warning/10 border border-warning/30 p-4 flex items-center gap-3 rounded-sm" data-testid="invite-used-warning">
            <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0" />
            <p className="text-sm">{t('loan.invite.used_warning') || 'This invite has already been used.'}</p>
          </div>
        )}

        {/* Loan Summary */}
        <div className="bg-white border border-border p-6 space-y-5">
          {lender && (
            <div className="flex items-center gap-3 pb-4 border-b border-border">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm font-semibold">{lender.name}</p>
                <p className="text-xs text-muted-foreground">{getTemplateLabel(templateType, 'principal')} &middot; {lender.phone_number}</p>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{getTemplateFieldLabel(templateType, 'principal_amount')}</span>
              <span className="font-serif text-xl font-bold">{formatCurrencyWithLang(loan?.principal_amount, lenderCurrency)}</span>
            </div>
            {hasInterest && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">{t('interest.rate')}</span>
                <div className="flex items-center gap-1.5">
                  <Percent className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="font-medium">{loan?.interest_rate}% {t(`interest.frequency_${loan?.interest_frequency?.toLowerCase()}`)}</span>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t('loan.detail.duration')}</span>
              <span className="font-medium">{loan?.duration_months} {t('common.months')}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t('loan.detail.start_date')}</span>
              <div className="flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="font-medium">{loan?.start_date}</span>
              </div>
            </div>
            {loan?.notes && (
              <div className="pt-3 border-t border-border">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">{t('loan.create.notes_label')}</p>
                <p className="text-sm">{loan.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Action */}
        {!invite?.used && (
          <div className="space-y-4">
            <div className="bg-primary/5 border border-primary/10 p-4 rounded-sm space-y-2">
              <div className="flex items-center gap-2">
                <Video className="w-4 h-4 text-primary" />
                <p className="text-sm font-semibold">{t('video.record.title')}</p>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('loan.invite.video_notice')}
              </p>
            </div>
            {user ? (
              <Button
                onClick={handleAccept}
                data-testid="accept-invite-button"
                disabled={accepting}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 rounded-sm uppercase tracking-wider font-semibold text-sm"
              >
                {accepting ? t('common.loading') : t('loan.invite.accept_button', { role: getTemplateLabel(templateType, 'secondary') })}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={() => navigate(`/login?redirect=/invite/${inviteToken}`)}
                data-testid="login-to-accept-button"
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-12 rounded-sm uppercase tracking-wider font-semibold text-sm"
              >
                {t('auth.login.title')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        )}

        <p className="text-xs text-muted-foreground/60 text-center">
          {t('disclaimer.text')}
        </p>
      </div>
    </div>
  );
}
